import { type FormEvent, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";

export function Reauthenticate({ onClose }: { onClose: () => void }) {
  const bypassTotp = import.meta.env.VITE_ADMIN_TOTP_BYPASS === "true";
  const [code, setCode] = useState(bypassTotp ? "000000" : "");
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (dialog.current?.showModal) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
    return () => dialog.current?.close?.();
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.reauth({ code, password });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not re-authenticate.");
    } finally {
      setPending(false);
    }
  }

  return (
    // Escape is handled by the native dialog; the click handler only adds backdrop dismissal.
    // biome-ignore lint/a11y/useKeyWithClickEvents: native dialog already has keyboard dismissal
    <dialog
      aria-labelledby="reauth-title"
      className="round-dialog reauth-dialog"
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      ref={dialog}
    >
      <form onSubmit={submit}>
        <div className="round-dialog-head">
          <div>
            <h2 className="heading-20" id="reauth-title">
              Confirm it’s you
            </h2>
            <p className="label-13 dim">Sensitive actions require a recent sign-in check.</p>
          </div>
          <button
            className="btn btn-quiet btn-sm"
            disabled={pending}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </div>
        <div className="round-dialog-body">
          <label className="field">
            <span>Password</span>
            <input
              autoComplete="current-password"
              autoFocus
              className="input"
              disabled={pending}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          {bypassTotp ? null : (
            <label className="field">
              <span>Authenticator code</span>
              <input
                autoComplete="one-time-code"
                className="input mono-12"
                disabled={pending}
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setCode(event.target.value)}
                pattern="[0-9]{6}"
                required
                value={code}
              />
            </label>
          )}
          {error ? (
            <p className="banner" role="alert">
              {error}
            </p>
          ) : null}
          <button className="btn" disabled={pending} type="submit">
            {pending ? "Checking…" : "Re-authenticate"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
